/**
 * « Imite ce smiley » en SOLO — machine à états PURE. Une série de `SMILEYS_PER_MATCH`
 * smileys tirés au sort ; pour chacun : visage au repos (décompte), 10 s d'imitation, puis
 * le score de la manche ; à la fin, la moyenne sur 100 (comparable au mode à deux).
 */
import { smileyAt, SMILEYS_PER_MATCH, type SmileyId } from './smileys';

export type SmileySoloPhase = 'intro' | 'rest' | 'imitate' | 'between' | 'over';

export interface SmileySoloState {
  readonly phase: SmileySoloPhase;
  /** Graine du tirage des smileys de la série. */
  readonly seed: number;
  /** Manche en cours (0..2) ; en `between` / `over`, la dernière jouée. */
  readonly round: number;
  /** Meilleur score tenu de chaque manche jouée. */
  readonly scores: readonly number[];
  /** Début de l'imitation en cours (ms). */
  readonly imitateStartedAt: number;
}

export type SmileySoloEvent =
  | { readonly type: 'start'; readonly seed: number }
  | { readonly type: 'restDone'; readonly t: number }
  | { readonly type: 'imitateDone'; readonly score: number }
  | { readonly type: 'next' }
  | { readonly type: 'restart'; readonly seed: number }
  | { readonly type: 'menu' };

export function createSmileySoloState(seed = 0): SmileySoloState {
  return { phase: 'intro', seed, round: 0, scores: [], imitateStartedAt: 0 };
}

const firstRound = (seed: number): SmileySoloState => ({
  ...createSmileySoloState(seed),
  phase: 'rest',
});

export function smileySoloStep(s: SmileySoloState, e: SmileySoloEvent): SmileySoloState {
  switch (e.type) {
    case 'start':
      return s.phase === 'intro' ? firstRound(e.seed) : s;
    case 'restDone':
      return s.phase === 'rest' ? { ...s, phase: 'imitate', imitateStartedAt: e.t } : s;
    case 'imitateDone': {
      if (s.phase !== 'imitate') return s;
      const scores = [...s.scores, clampScore(e.score)];
      return { ...s, scores, phase: scores.length >= SMILEYS_PER_MATCH ? 'over' : 'between' };
    }
    case 'next':
      return s.phase === 'between' ? { ...s, phase: 'rest', round: s.round + 1 } : s;
    case 'restart':
      return s.phase === 'over' ? firstRound(e.seed) : s;
    case 'menu':
      return s.phase === 'intro' ? s : createSmileySoloState(s.seed);
  }
}

const clampScore = (score: number): number =>
  Number.isFinite(score) ? Math.min(100, Math.max(0, Math.round(score))) : 0;

/** Smiley de la manche en cours (ou de la dernière jouée). */
export const currentSmiley = (s: SmileySoloState): SmileyId => smileyAt(s.seed, 0, s.round);

/** Score final : moyenne des manches jouées, arrondie (0 si aucune). */
export function averageScore(scores: readonly number[]): number {
  if (scores.length === 0) return 0;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}
